/**
 * Buyer listing visibility — existing DB fields only (no schema changes).
 * Upcoming projects: show all by default; hide only when agent Hide set
 * upcoming_project_data.isVisible = false.
 * Regular listings: hide only on manual Hide while visibility_end_date is still in the future.
 */

export const isUpcomingProject = (property) => {
  const projectType = String(property?.project_type ?? property?.projectType ?? '').toLowerCase();
  return projectType === 'upcoming';
};

export const isUpcomingProjectRequest = (params = {}) =>
  String(params.project_type || params.projectType || '').toLowerCase() === 'upcoming';

const parseVisibilityEnd = (property) => {
  const raw = property?.visibility_end_date ?? property?.visibilityEndDate;
  if (!raw) return null;
  const date = new Date(raw);
  return Number.isNaN(date.getTime()) ? null : date;
};

const parseUpcomingProjectData = (property) => {
  let data = property?.upcoming_project_data ?? property?.upcomingProjectData ?? property?.upcomingData;
  if (!data) return {};
  if (typeof data === 'string') {
    try {
      data = JSON.parse(data);
    } catch {
      return {};
    }
  }
  return data && typeof data === 'object' ? data : {};
};

/**
 * Upcoming projects: visible by default.
 * Hidden only when agent clicked Hide (isVisible === false).
 * Do NOT hide based on is_active alone (legacy projects often have is_active = 0).
 */
export const isUpcomingProjectVisibleToBuyers = (property) => {
  if (!property || typeof property !== 'object') return false;
  const upcomingData = parseUpcomingProjectData(property);
  if (
    upcomingData.isVisible === false
    || upcomingData.isVisible === 0
    || upcomingData.isVisible === '0'
    || upcomingData.isVisible === 'false'
  ) {
    return false;
  }
  return true;
};

export const filterUpcomingBuyerProjects = (properties) => {
  if (!Array.isArray(properties)) return [];
  return properties.filter(isUpcomingProjectVisibleToBuyers);
};

/** Matches backend property_visibility.php logic for regular listings. */
export const isRegularListingVisibleToBuyers = (property) => {
  if (!property || typeof property !== 'object') return false;
  if (isUpcomingProject(property)) return true;

  const active = property.is_active ?? property.isActive;
  if (active === true || active === 1 || active === '1') return true;

  const endDate = parseVisibilityEnd(property);
  if (!endDate) return true;

  return endDate.getTime() < Date.now();
};

export const filterRegularBuyerProperties = (properties) => {
  if (!Array.isArray(properties)) return [];
  return properties.filter(isRegularListingVisibleToBuyers);
};

export const adjustPaginationAfterFilter = (pagination, removedCount, fallbackLength = 0) => {
  if (!pagination || removedCount <= 0) return pagination;

  const limit = Number(pagination.limit) || fallbackLength || 1;
  const nextTotal = Math.max(0, (Number(pagination.total) || fallbackLength) - removedCount);

  return {
    ...pagination,
    total: nextTotal,
    total_pages: Math.max(1, Math.ceil(nextTotal / limit)),
  };
};

/** Normalize listing status to API key: sale | rent | '' */
export const normalizeListingStatusKey = (value) => {
  const normalized = String(value || '').toLowerCase().replace(/\s+/g, '');
  if (normalized.includes('rent')) return 'rent';
  if (normalized.includes('sale') || normalized === 'buy') return 'sale';
  return '';
};

export const formatListingStatusLabel = (statusKey) => {
  if (statusKey === 'rent') return 'For Rent';
  if (statusKey === 'sale') return 'For Sale';
  return '';
};

export const resolveListingStatusFromSearchParams = (searchParams) => {
  if (!searchParams) return '';

  let status = searchParams.get('status') || '';
  const propertyType = (searchParams.get('propertyType') || '').toLowerCase();

  if (propertyType === 'buy') status = status || 'sale';
  else if (propertyType === 'rent') status = status || 'rent';

  return normalizeListingStatusKey(status);
};

/** Seller/agent dashboard Hide/Show toggle state. */
export const isSellerToggleVisible = (property) => {
  const active = property?.isActive ?? property?.is_active;
  return active === true || active === 1 || active === '1';
};
